import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { RotateCcw } from 'lucide-react';
import { AmendmentModel, assertNever } from '@signalvote/shared';
import type { ComplianceReportDetailDto, ReopenWarning } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCampaignStore } from '@/stores';
import { useAmendReport, useFilingChain, useReopenAdvisory, useReopenReport } from '@/hooks/queries/useFinanceReports';
import { openAmendment } from './reportUtils';
import { ReopenPeriodAffordance } from './ReopenPeriodAffordance';
import { FilingLockCopy, continueAmendmentMessage } from './filingLockCopy';

/**
 * What "yes, I filed it with the state" leads to, by the jurisdiction's declared amendment model
 * (ADR-021). Where the app rebuilds the report, the next step is an amendment, which opens the
 * period itself. Where corrections are made in the portal, it is the period unlock. An unresolved
 * profile offers no amendment: an offer site coalesces an undeclared policy to "don't offer"
 * (ADR-078).
 */
function filedWithStateNextStep(model: AmendmentModel | null): 'startAmendment' | 'unlockPeriod' {
  if (model === null) return 'unlockPeriod';
  switch (model) {
    case AmendmentModel.SelfContainedUpload:
    case AmendmentModel.ElectronicResubmit:
      return 'startAmendment';
    case AmendmentModel.PortalOnly:
      return 'unlockPeriod';
    default:
      return assertNever(model);
  }
}

/**
 * Changing a report the treasurer marked filed (ADR-023, ADR-064). The dialog asks one question,
 * "did you file this with the state?":
 *   • Yes → the report stays filed. The reopen endpoint refuses a yes, so this never calls it.
 *     Where the app rebuilds the amendment, the dialog closes onto "Start the amendment", which
 *     creates it (opening its period) and goes to it. Where corrections are made in the portal,
 *     it closes onto the period unlock (ADR-019 as amended 2026-10-14 and 2026-10-21).
 *   • No, I marked it filed by mistake → the reopen undoes the filed mark and the period opens.
 *
 * A pre-action advisory (`useReopenAdvisory`) picks the lead copy (a filing on record vs an
 * in-app-only no-activity close) and names later Closed periods whose opening balances may go stale.
 *
 * Gated on Finance/Manage, matching the FileReport gate; the server's `[RequireCapability]` is the
 * real boundary. Self-hides unless `canReopen`.
 */
export function ReopenReportAffordance({
  report,
  amendmentModel,
}: {
  report: ComplianceReportDetailDto;
  amendmentModel: AmendmentModel | null;
}) {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const navigate = useNavigate();
  const { reopenReport } = useReopenReport(report.id);
  const { amendReport } = useAmendReport();
  const [amending, setAmending] = useState(false);
  const [amendError, setAmendError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // Fetch the advisory only once the dialog is open (deferred; staleTime=0).
  const { data: advisory } = useReopenAdvisory(report.id, open);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultWarnings, setResultWarnings] = useState<ReopenWarning[] | null>(null);
  const [filedWithState, setFiledWithState] = useState(false);
  const { data: chain } = useFilingChain(filedWithState ? report.id : null);
  const inProgress = openAmendment(chain?.chain);

  if (!canManage || !report.canReopen) return null;

  const leadsWithAmendment = advisory?.leadsWithAmendment ?? true;
  const laterClosedNames = advisory?.laterClosedPeriodNames ?? [];

  const resetTransient = () => {
    setError(null);
    setResultWarnings(null);
  };

  const handleFiledWithState = () => {
    resetTransient();
    setFiledWithState(true);
    setOpen(false);
  };

  const handleStartAmendment = async () => {
    setAmending(true);
    setAmendError(null);
    try {
      const reportId =
        inProgress?.complianceReportId ??
        (await amendReport({ reportId: report.id, concurrencyVersion: report.concurrencyVersion }));
      navigate({ to: '/admin/finance/reports/$reportId', params: { reportId } });
    } catch (e) {
      setAmendError(e instanceof Error ? e.message : 'Failed to start the amendment');
    } finally {
      setAmending(false);
    }
  };

  const handleReopen = async () => {
    setBusy(true);
    resetTransient();
    try {
      // Echo the report's EF token — never hardcode 0 (a mismatch is a 409).
      const result = await reopenReport({
        reportId: report.id,
        concurrencyVersion: report.concurrencyVersion,
        filedWithState: false,
      });
      // Keep the dialog open only if there is a warning to read.
      if (result.warnings.length > 0) {
        setResultWarnings(result.warnings);
      } else {
        setOpen(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reopen the report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="reopen-report-affordance">
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          resetTransient();
          setFiledWithState(false);
          setOpen(true);
        }}
        disabled={busy}
        data-testid="reopen-report-button"
      >
        <RotateCcw className="mr-1 h-4 w-4" />
        Change this report
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change this closed report</AlertDialogTitle>
            <AlertDialogDescription data-testid="reopen-report-confirm-body">
              {leadsWithAmendment ? (
                <>
                  Did you file this report with the state? If you did, it stays filed here too, and
                  any change you make is an <strong>amendment</strong> you also file with the state.
                  If you marked it filed by mistake, we&apos;ll undo that and the period opens again.
                </>
              ) : (
                <>
                  This period was closed with <strong>no activity</strong> and nothing was sent to
                  the state, so undoing the close is a clean undo: the period simply returns to open.
                  Did you file this report with the state yourself?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {laterClosedNames.length > 0 && (
            <p
              className="text-sm text-amber-700"
              data-testid="reopen-later-periods-warning"
            >
              Heads up: later reporting {laterClosedNames.length === 1 ? 'period is' : 'periods are'}{' '}
              already closed ({laterClosedNames.join(', ')}). Their opening balances won't update
              automatically — reopen them too if you need to correct the carry-forward.
            </p>
          )}

          {resultWarnings && resultWarnings.length > 0 && (
            <div className="space-y-1" data-testid="reopen-result-warnings">
              {resultWarnings.map((w) => (
                <p key={w.code} className="text-sm text-amber-700" data-testid="reopen-result-warning">
                  {w.message}
                </p>
              ))}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive" data-testid="reopen-report-error">
              {error}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reopen-report-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reopen-choose-filed"
              onClick={(e) => {
                e.preventDefault();
                handleFiledWithState();
              }}
            >
              Yes, I filed it with the state
            </AlertDialogAction>
            <AlertDialogAction
              data-testid="reopen-choose-reopen"
              onClick={(e) => {
                e.preventDefault();
                void handleReopen();
              }}
            >
              No, I marked it filed by mistake
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {filedWithState &&
        (filedWithStateNextStep(amendmentModel) === 'startAmendment' &&
        (report.locked || report.manuallyUnlocked) ? (
          <div
            className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
            data-testid="reopen-start-amendment-panel"
          >
            <p className="text-sm text-amber-900">
              {inProgress
                ? continueAmendmentMessage(inProgress.amendmentNumber)
                : FilingLockCopy.StartAmendmentMessage}
            </p>
            {amendError && (
              <p className="text-sm text-destructive" role="alert" data-testid="reopen-amend-error">
                {amendError}
              </p>
            )}
            <Button
              size="sm"
              onClick={() => void handleStartAmendment()}
              disabled={amending}
              data-testid="reopen-start-amendment"
            >
              {inProgress
                ? `Continue Amendment #${inProgress.amendmentNumber}`
                : FilingLockCopy.StartAmendmentButton}
            </Button>
          </div>
        ) : report.locked ? (
          <ReopenPeriodAffordance report={report} initiallyExpanded />
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="reopen-filed-open-note">
            You can change this period&apos;s transactions now. Because the report is already filed
            with the state, file your changes there as an amendment.
          </p>
        ))}
    </div>
  );
}
